import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MENU_SECTIONS, BEST_SELLERS, TONE_BG, type FeatureCard, type MenuLink, type MenuSection } from './menuData';
import { useMenuPrices, trackMenuClick } from './useMenuPrices';

const CLOSE_DELAY = 180;

export const DesktopMegaMenu = () => {
  const [openId, setOpenId] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const cancel = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  const open = (id: string) => { cancel(); setOpenId(id); };
  const scheduleClose = () => { cancel(); timer.current = setTimeout(() => setOpenId(null), CLOSE_DELAY); };
  const close = useCallback(() => { cancel(); setOpenId(null); }, []);

  useEffect(() => {
    if (!openId) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    const onDown = (e: MouseEvent) => { if (rootRef.current && !rootRef.current.contains(e.target as Node)) close(); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown); };
  }, [openId, close]);

  useEffect(() => cancel, []);

  const active = MENU_SECTIONS.find((s) => s.id === openId);

  return (
    <div ref={rootRef} className="hidden min-[980px]:flex items-center ml-8" onMouseLeave={scheduleClose} onMouseEnter={cancel}>
      <nav aria-label="Menu principal" className="flex items-center gap-1">
        {MENU_SECTIONS.map((s) => (
          <button
            key={s.id}
            type="button"
            onMouseEnter={() => open(s.id)}
            onClick={() => (openId === s.id ? close() : open(s.id))}
            aria-expanded={openId === s.id}
            aria-controls={`mega-${s.id}`}
            aria-haspopup="true"
            className={cn(
              'flex items-center gap-1 px-3 py-2 text-[15px] font-medium text-foreground hover:text-primary transition-colors whitespace-nowrap rounded-md',
              openId === s.id && 'text-primary',
            )}
          >
            {s.label}
            <ChevronDown className={cn('w-4 h-4 transition-transform', openId === s.id && 'rotate-180')} aria-hidden />
          </button>
        ))}
      </nav>

      {/* Panel spans container width under the header */}
      <div className="absolute left-0 right-0 top-full pt-2" style={{ pointerEvents: active ? 'auto' : 'none' }}>
        <div className="container mx-auto px-4">
          {MENU_SECTIONS.map((s) => (
            <div
              key={s.id}
              id={`mega-${s.id}`}
              role="region"
              aria-label={s.label}
              hidden={openId !== s.id}
              className="bg-background rounded-2xl shadow-[0_18px_50px_-20px_hsl(var(--v2-ink)/0.35)] border border-border/60 p-8 animate-mega-in"
            >
              <Panel section={s} onNavigate={close} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ListItem = ({ link, section, onNavigate, dot }: { link: MenuLink; section: string; onNavigate: () => void; dot?: boolean }) => (
  <Link
    to={link.href}
    onClick={() => { trackMenuClick(`${section}:${link.title}`, link.href, 'desktop'); onNavigate(); }}
    className="flex items-start gap-3 rounded-lg px-3 py-2.5 hover:bg-v2-bg focus-visible:bg-v2-bg transition-colors"
  >
    {dot && link.tone && <span className={cn('mt-1.5 h-3 w-3 shrink-0 rounded-full', TONE_BG[link.tone])} aria-hidden />}
    <span>
      <span className="block font-semibold text-foreground leading-snug">{link.title}</span>
      {link.desc && <span className="block text-sm text-v2-muted leading-snug">{link.desc}</span>}
    </span>
  </Link>
);

const Panel = ({ section, onNavigate }: { section: MenuSection; onNavigate: () => void }) => {
  const price = useMenuPrices();
  return (
    <div className="grid grid-cols-[1fr_1fr_300px] gap-8">
      {section.columns.map((col) => (
        <div key={col.heading} className={cn(col.grid && !section.bestSellers ? 'col-span-2' : '')}>
          <p className="mb-3 px-3 text-xs font-semibold uppercase tracking-wider text-v2-muted">{col.heading}</p>
          <div className={cn(col.grid ? 'grid grid-cols-2 gap-x-2' : 'flex flex-col')}>
            {col.links.map((l) => <ListItem key={l.href + l.title} link={l} section={section.label} onNavigate={onNavigate} dot={col.grid} />)}
          </div>
          {col.footerLink && (
            <Link
              to={col.footerLink.href}
              onClick={() => { trackMenuClick(`${section.label}:ver_todos`, col.footerLink!.href, 'desktop'); onNavigate(); }}
              className="mt-3 inline-block px-3 text-sm font-semibold text-primary hover:underline"
            >
              {col.footerLink.title}
            </Link>
          )}
        </div>
      ))}

      {section.bestSellers && (
        <div>
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-v2-muted">Mais vendidos</p>
          <div className="grid grid-cols-3 gap-3">
            {BEST_SELLERS.slice(0, 3).map((p) => (
              <Link
                key={p.handle}
                to={`/produto/${p.handle}`}
                onClick={() => { trackMenuClick(`Loja:mais_vendidos:${p.name}`, `/produto/${p.handle}`, 'desktop'); onNavigate(); }}
                className="group block rounded-xl overflow-hidden border border-border/60 bg-background hover:shadow-md transition-shadow"
              >
                <div className={cn('h-1', TONE_BG[p.tone])} aria-hidden />
                <img src={p.image} alt="" width={160} height={160} loading="lazy" decoding="async" className="aspect-square w-full object-cover bg-white" />
                <div className="p-2">
                  <p className="text-sm font-semibold leading-snug text-foreground">{p.name}</p>
                  {price(p.handle) && <p className="text-sm text-v2-muted mt-0.5">{price(p.handle)}</p>}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <Card card={section.card} section={section.label} onNavigate={onNavigate} />
    </div>
  );
};

const Card = ({ card, section, onNavigate }: { card: FeatureCard; section: string; onNavigate: () => void }) => {
  const click = () => { trackMenuClick(`${section}:destaque`, card.href, 'desktop'); onNavigate(); };
  if (card.kind === 'quiz') {
    return (
      <div className="rounded-2xl bg-v2-blue/10 p-6 flex flex-col">
        <span className="self-start rounded-full bg-background px-3 py-1 text-xs font-semibold text-v2-ink">{card.badge}</span>
        <p className="mt-4 text-xl font-narrow font-bold text-v2-ink">{card.title}</p>
        <p className="mt-2 text-sm text-v2-muted">{card.text}</p>
        <Link to={card.href} onClick={click} className="mt-auto pt-5">
          <span className="inline-flex min-h-12 items-center rounded-full bg-v2-ink px-5 font-semibold text-background hover:opacity-90">{card.cta}</span>
        </Link>
      </div>
    );
  }
  if (card.kind === 'dark') {
    return (
      <div className="rounded-2xl bg-v2-ink p-6 flex flex-col text-background">
        <img src={card.avatar} alt="Rafael Cossiello" width={56} height={56} loading="lazy" decoding="async" className="h-14 w-14 rounded-full object-cover" />
        <p className="mt-4 text-xl font-narrow font-bold">{card.title}</p>
        <p className="mt-2 text-sm opacity-90">{card.text}</p>
        <Link to={card.href} onClick={click} className="mt-auto pt-5">
          <span className="inline-flex min-h-12 items-center rounded-full bg-background px-5 font-semibold text-v2-ink hover:opacity-90">{card.cta}</span>
        </Link>
      </div>
    );
  }
  return (
    <Link to={card.href} onClick={click} className="relative block min-h-[260px] overflow-hidden rounded-2xl group">
      <img src={card.image} alt="" width={300} height={300} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-v2-ink/90 via-v2-ink/30 to-transparent" aria-hidden />
      <div className="absolute inset-x-0 bottom-0 p-5 text-background">
        <span className="inline-block rounded-full bg-background/90 px-3 py-1 text-xs font-semibold text-v2-ink">{card.badge}</span>
        <p className="mt-3 text-lg font-narrow font-bold leading-snug">{card.title}</p>
        {card.text && <p className="mt-1 text-sm opacity-90">{card.text}</p>}
      </div>
    </Link>
  );
};
